/**
 * Последний удачный ответ адреса — для экранов, которые ПЕРЕЧИТЫВАЮТ ресурс в фоне (ревью
 * партии 19.09, находки U-B3 и U-B5).
 *
 * `useResource` на сбое перечитывания сбрасывает данные, и для него это верно: его контракт —
 * «данные ИЛИ ошибка», а экран черновика обязан не показывать чужое (F-087). Но экран, который
 * перечитывает то же самое после каждого действия или по таймеру, от одного неудачного GET
 * терял всё: редактор стилей прятал открытую правку за ошибкой, сравнение стилей меняло пару
 * превью на ошибку во всю страницу и переставало опрашивать. Здесь помнится последний удачный
 * разбор ТОГО ЖЕ адреса: сбой перечитывания отдаёт прежнее вместе с причиной «не обновилось», а
 * сбой нового адреса — по-прежнему ошибка на месте данных, без чужого прошлого.
 *
 * ЧЕЙ ОТВЕТ СЕЙЧАС В РЕСУРСЕ. `useResource` сбрасывает прежние данные ЭФФЕКТОМ, то есть в
 * отрисовке, где адрес уже новый, ресурс ещё держит ответ старого. Запомни хук его под новым
 * адресом — и сбой нового адреса показал бы содержимое старого как «последнее загруженное».
 * Поэтому пара (данные, ошибка) приписывается адресу, при котором она ПОЯВИЛАСЬ, а в отрисовке
 * смены адреса хук не отдаёт ничего нового: ни прежних данных, ни прежней ошибки.
 *
 * Хук — обёртка, а не правка `useResource`: тот «целиком, расширять без нужды нельзя»
 * (05-ui.md §1.3), и остальные экраны живут по его контракту.
 */

import { useState } from 'react';
import type { Resource } from './useResource.ts';

export interface LastGood<T> {
  /** Что рисовать: свежий разбор, а после сбоя перечитывания — последний удачный того же адреса. */
  readonly value: T | null;
  /**
   * Почему последний ответ адреса не показан: текст отказа или «ответ сервера не разобран».
   * `value !== null` вместе с ним — показано прежнее (плашка «не обновилось»); `value === null` —
   * показать нечего (ошибка на месте данных).
   */
  readonly failure: string | null;
}

interface Memory<T> {
  /** Пара ресурса, которую хук уже видел, и адрес, при котором она появилась. */
  readonly data: unknown;
  readonly error: Error | undefined;
  readonly owner: string | null;
  /** Последний удачный разбор и его адрес. */
  readonly good: { readonly path: string; readonly value: T } | null;
}

/**
 * `fresh` — разбор `res.data` схемой вызывающего, ОБЯЗАТЕЛЬНО мемоизированный по `res.data`
 * (`useMemo`): новая пара ресурса узнаётся по тождеству, и разбор, пересоздаваемый на каждой
 * отрисовке, тут ни при чём, а вот `null`/не-`null` у него должен соответствовать данным.
 */
export function useLastGood<T>(
  path: string | null,
  res: Resource<unknown>,
  fresh: T | null,
): LastGood<T> {
  const [memory, setMemory] = useState<Memory<T>>(() => ({
    data: res.data,
    error: res.error,
    owner: path,
    good: fresh !== null && path !== null ? { path, value: fresh } : null,
  }));

  let now = memory;
  if (memory.data !== res.data || memory.error !== res.error) {
    // Новая пара — ответ текущего адреса: запрос прежнего `useResource` снимает вместе со сменой.
    now = {
      data: res.data,
      error: res.error,
      owner: path,
      good: fresh !== null && path !== null ? { path, value: fresh } : memory.good,
    };
    // Запомнить до отрисовки детей: React перерисует компонент сразу, не показывая этот проход.
    setMemory(now);
  }

  // Отрисовка смены адреса: в ресурсе ещё ответ прежнего — ни его данных, ни его ошибки. Для
  // экрана это «загружаем», ровно как в следующей отрисовке, когда ресурс сбросится сам.
  if (now.owner !== path) return { value: null, failure: null };
  if (fresh !== null) return { value: fresh, failure: null };
  const kept = now.good !== null && now.good.path === path ? now.good.value : null;
  const failure =
    res.error !== undefined
      ? res.error.message
      : res.data !== undefined
        ? 'ответ сервера не разобран'
        : null;
  return { value: failure === null ? null : kept, failure };
}
